import { CRMatrixRole, StatusCampaign, StatusSubCampaign, WorkflowType } from '@/types/Matrix';
import {
  statusCampaign as statusCR, ADMIN_CR, CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR,
  INFORMER_APPROVER_CR, statusSubCampaign as statusSubCR,
  CAMPAIGN_MANAGER_CR, CAUSER_PRE_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, INFORMER_CHECK_APPROVER_CR,
  STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, QMB_PLANT_APPROVER_CR, E4_PLANT_SUPERVISOR_CR, APPROVER_CR,
  CENTER_VIEWER_CR, REPORTER_CR, COMMITTEE_COORDINATOR_CR, CAMPAIGN_RECIPIENT_CR, VAN_COMMITTEE_COORDINATOR_CR,
  VAN_E3_QMB_PLANT_APPROVER_CR, VAN_E2_QMB_APPROVER_CR, VAN_CAMPAIGN_MANAGER_CR, VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR, WORKFLOW_TYPE,
  CREATOR_EXTERNAL_CR, PLANT_SUPERVISOR_CR,
} from './constants';

type Status = StatusCampaign | StatusSubCampaign;

type MatrixRole = CRMatrixRole;

type MatrixByStatus = { [status in Status]?: MatrixRole[] };

type MatrixByWorkflow = { [workflow in WorkflowType]?: MatrixByStatus };

type CRPermissionMatrix = { [action in CR_PERMISSION_ACTIONS]: MatrixByStatus | MatrixByWorkflow };

export enum CR_PERMISSION_ACTIONS {
  EDIT_TITLE = 'editTitle',
  DELETE = 'delete',
  SAVE = 'save',
  SHOW_SUMMARY = 'showSummary',
  FULL_CR = 'fullCR',
  SEND_TO_APPROVAL = 'sendToApproval',
  SEND_TO_REVIEW = 'sendToReview',
  MAKE_DECISION = 'makeDecision',
  ASSIGN = 'assign',
  ASSIGN_TO = 'assignTo',
  CONFIRM_KNOWLEDGE = 'confirmKnowledge',
  COPY_SUB_CR = 'copySubCR',
  ASSIGN_MANAGER_OVERVIEW = 'assignManagerOverview',
  ASSIGN_MANAGER = 'assignManager',
  MAKE_DECISION_SUB_CR = 'makeDecisionSubCR',
  SEND_TO_APPROVAL_SUB_CR = 'sendToApprovalSubCR',
  FULL_CR_SUB_CR = 'fullCRSubCR',
  SHOW_SUMMARY_SUB_CR = 'showSummarySubCR',
  COMPLETE_SUB_CR = 'completeSubCR',
  ASSIGN_CREATOR = 'assignCreator',
  TRANSLATIONS_TAB = 'translationsTab',
  PLANT_FEEDBACK_TAB = 'plantFeedbackTab',
  SHOW_CHANGES = 'showChanges',
  SPLIT_SUB_CAMPAIGN = 'splitSubCampaign',
  UPLOAD_ATTACHMENT = 'uploadAttachment',
};

const assignManagerPermissions: MatrixByWorkflow = {
  [WORKFLOW_TYPE.DIGITAL]: {
    [statusSubCR.OPEN]: [CAMPAIGN_MANAGER_CR, ADMIN_CR],
    [statusSubCR.IN_CHECKING]: [CAMPAIGN_MANAGER_CR, ADMIN_CR],
    [statusSubCR.IN_ANALYSIS]: [CAMPAIGN_MANAGER_CR, ADMIN_CR],
  },
  [WORKFLOW_TYPE.NOT_READY]: {
    [statusSubCR.OPEN]: [CAMPAIGN_MANAGER_CR, ADMIN_CR],
    [statusSubCR.IN_ANALYSIS]: [CAMPAIGN_MANAGER_CR, ADMIN_CR],
    [statusSubCR.IN_PLANT_PREPARATION]: [ADMIN_CR, VAN_CAMPAIGN_MANAGER_CR],
  },
};

const matrix: CRPermissionMatrix = {
  [CR_PERMISSION_ACTIONS.EDIT_TITLE]: {
    [statusCR.IN_CREATION]: [CREATOR_CR, CREATOR_EXTERNAL_CR, ADMIN_CR, STAND_IN_CR],
    [statusCR.IN_CAUSER_APPROVAL]: [ADMIN_CR],
    [statusCR.IN_CAUSER_PRE_APPROVAL]: [ADMIN_CR],
    [statusCR.IN_CAUSER_CHECK]: [ADMIN_CR],
    [statusCR.IN_INFORMER_APPROVAL]: [ADMIN_CR],
    [statusCR.IN_INFORMER_PRE_APPROVAL]: [ADMIN_CR],
    [statusCR.IN_INFORMER_CHECK]: [ADMIN_CR],
    [statusCR.IN_PLANT_VALIDATION]: [ADMIN_CR],
    [statusCR.IN_COMMITTEE]: [ADMIN_CR],
    [statusCR.IN_PLANT_APPROVAL]: [ADMIN_CR],
    [statusCR.REQUESTED_FOR_CHANGES]: [ADMIN_CR],
    [statusCR.IN_RD_COMMITTEE]: [ADMIN_CR],
    [statusCR.IN_PREPARATION_FOR_REVIEW]: [ADMIN_CR],
    [statusCR.IN_VAN_PLANT_VALIDATION]: [ADMIN_CR],
    [statusCR.IN_PLANT_IMPLEMENTATION]: [CREATOR_CR, ADMIN_CR],
    [statusCR.IN_VAN_PLANT_IMPLEMENTATION]: [CREATOR_CR, ADMIN_CR],
    [statusCR.IN_REVIEW]: [ADMIN_CR],
  },
  [CR_PERMISSION_ACTIONS.DELETE]: {
    [statusCR.IN_CREATION]: [CREATOR_CR, CREATOR_EXTERNAL_CR, ADMIN_CR],
    [statusCR.IN_CAUSER_APPROVAL]: [ADMIN_CR],
    [statusCR.IN_CAUSER_PRE_APPROVAL]: [ADMIN_CR],
    [statusCR.IN_CAUSER_CHECK]: [ADMIN_CR],
    [statusCR.IN_INFORMER_APPROVAL]: [ADMIN_CR],
    [statusCR.IN_INFORMER_PRE_APPROVAL]: [ADMIN_CR],
    [statusCR.IN_INFORMER_CHECK]: [ADMIN_CR],
    [statusCR.IN_PLANT_VALIDATION]: [ADMIN_CR],
    [statusCR.IN_COMMITTEE]: [ADMIN_CR],
    [statusCR.IN_PLANT_APPROVAL]: [ADMIN_CR],
    [statusCR.REQUESTED_FOR_CHANGES]: [ADMIN_CR],
    [statusCR.DELETED]: [CREATOR_CR, ADMIN_CR],
    [statusCR.IN_RD_COMMITTEE]: [ADMIN_CR],
    [statusCR.IN_PREPARATION_FOR_REVIEW]: [ADMIN_CR],
    [statusCR.IN_VAN_PLANT_VALIDATION]: [ADMIN_CR],
    [statusCR.IN_PLANT_IMPLEMENTATION]: [ADMIN_CR],
    [statusCR.IN_VAN_PLANT_IMPLEMENTATION]: [ADMIN_CR],
    [statusCR.IN_REVIEW]: [ADMIN_CR],
    [statusCR.CLOSED]: [CREATOR_CR, ADMIN_CR],
    [statusCR.COMPLETED]: [CREATOR_CR, ADMIN_CR],
  },
  [CR_PERMISSION_ACTIONS.SAVE]: {
    [statusCR.IN_CREATION]: [CREATOR_CR, CREATOR_EXTERNAL_CR, ADMIN_CR],
    [statusCR.IN_CAUSER_APPROVAL]: [ADMIN_CR],
    [statusCR.IN_CAUSER_PRE_APPROVAL]: [ADMIN_CR],
    [statusCR.IN_CAUSER_CHECK]: [ADMIN_CR],
    [statusCR.IN_INFORMER_APPROVAL]: [ADMIN_CR],
    [statusCR.IN_INFORMER_PRE_APPROVAL]: [ADMIN_CR],
    [statusCR.IN_INFORMER_CHECK]: [ADMIN_CR],
    [statusCR.IN_PLANT_VALIDATION]: [ADMIN_CR],
    [statusCR.IN_COMMITTEE]: [ADMIN_CR],
    [statusCR.IN_PLANT_APPROVAL]: [ADMIN_CR],
    [statusCR.IN_RD_COMMITTEE]: [ADMIN_CR],
  },
  [CR_PERMISSION_ACTIONS.SHOW_SUMMARY]: {
    [statusCR.IN_CREATION]: [CREATOR_CR, CREATOR_EXTERNAL_CR, REQUESTER_CR, ADMIN_CR, STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, REPORTER_CR],
    [statusCR.IN_CAUSER_APPROVAL]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR,
      STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, APPROVER_CR, CENTER_VIEWER_CR, REPORTER_CR,
      CAUSER_CHECK_APPROVER_CR, INFORMER_CHECK_APPROVER_CR, CAUSER_PRE_APPROVER_CR, INFORMER_PRE_APPROVER_CR],
    [statusCR.IN_INFORMER_APPROVAL]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR,
      STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, APPROVER_CR, CENTER_VIEWER_CR, REPORTER_CR,
      CAUSER_CHECK_APPROVER_CR, INFORMER_CHECK_APPROVER_CR, CAUSER_PRE_APPROVER_CR, INFORMER_PRE_APPROVER_CR],
    [statusCR.IN_CAUSER_PRE_APPROVAL]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR,
      STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, CAUSER_PRE_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CENTER_VIEWER_CR, REPORTER_CR],
    [statusCR.IN_INFORMER_PRE_APPROVAL]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR,
      STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, CAUSER_PRE_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CENTER_VIEWER_CR, REPORTER_CR],
    [statusCR.IN_CAUSER_CHECK]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR,
      STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, CAUSER_CHECK_APPROVER_CR, INFORMER_CHECK_APPROVER_CR, CENTER_VIEWER_CR, REPORTER_CR],
    [statusCR.IN_INFORMER_CHECK]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR,
      STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, CAUSER_CHECK_APPROVER_CR, INFORMER_CHECK_APPROVER_CR, CENTER_VIEWER_CR, REPORTER_CR],
    [statusCR.IN_PLANT_VALIDATION]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR,
      STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, APPROVER_CR, CAUSER_PRE_APPROVER_CR, INFORMER_PRE_APPROVER_CR,
      CAUSER_CHECK_APPROVER_CR, INFORMER_CHECK_APPROVER_CR, CENTER_VIEWER_CR, REPORTER_CR],
    [statusCR.IN_COMMITTEE]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR, STAND_IN_CR,
      CENTER_VIEWER_CR, REPORTER_CR],
    [statusCR.IN_PLANT_APPROVAL]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR,
      STAND_IN_CR, REPORTER_CR],
    [statusCR.CLOSED]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR, STAND_IN_CR,
      CENTER_VIEWER_CR, REPORTER_CR, RD_COMMITTEE_COORDINATOR_CR, VAN_CAMPAIGN_MANAGER_CR, VAN_COMMITTEE_COORDINATOR_CR,
      REPORTER_CR],
    [statusCR.DELETED]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR, STAND_IN_CR,
      CENTER_VIEWER_CR, REPORTER_CR, RD_COMMITTEE_COORDINATOR_CR],
    [statusCR.COMPLETED]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR, STAND_IN_CR,
      CENTER_VIEWER_CR, REPORTER_CR, CAUSER_CHECK_APPROVER_CR, INFORMER_CHECK_APPROVER_CR, CAUSER_PRE_APPROVER_CR,
      INFORMER_PRE_APPROVER_CR, APPROVER_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E2_QMB_APPROVER_CR, VAN_CAMPAIGN_MANAGER_CR,
      RD_COMMITTEE_COORDINATOR_CR],
    [statusCR.REQUESTED_FOR_CHANGES]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR, STAND_IN_CR,
      CENTER_VIEWER_CR, REPORTER_CR, RD_COMMITTEE_COORDINATOR_CR],
    [statusCR.IN_RD_COMMITTEE]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR, STAND_IN_CR,
      RD_COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, REPORTER_CR, CAUSER_CHECK_APPROVER_CR,
      INFORMER_CHECK_APPROVER_CR, CAUSER_PRE_APPROVER_CR, INFORMER_PRE_APPROVER_CR, APPROVER_CR],
    [statusCR.IN_PREPARATION_FOR_REVIEW]: [ADMIN_CR, CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, REPORTER_CR],
    [statusCR.IN_REVIEW]: [ADMIN_CR, CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, REPORTER_CR],
    [statusCR.IN_VAN_CAUSER_APPROVAL]: [ADMIN_CR, CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, REPORTER_CR],
    [statusCR.IN_CARLINE_CONFIRMATION]: [ADMIN_CR, CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR,
      REPORTER_CR],
    [statusCR.IN_QMB_VAN_INFORMATION]: [ADMIN_CR, CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR,
      VAN_E3_QMB_PLANT_APPROVER_CR, VAN_E2_QMB_APPROVER_CR, VAN_CAMPAIGN_MANAGER_CR, REPORTER_CR],
    [statusCR.IN_VAN_PLANT_VALIDATION]: [CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR,
      VAN_E3_QMB_PLANT_APPROVER_CR, VAN_E2_QMB_APPROVER_CR, VAN_CAMPAIGN_MANAGER_CR, ADMIN_CR, REPORTER_CR],
    [statusCR.IN_PLANT_IMPLEMENTATION]: [CREATOR_CR, ADMIN_CR, APPROVER_CR, REPORTER_CR],
    [statusCR.IN_VAN_PLANT_IMPLEMENTATION]: [CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR,
      VAN_E3_QMB_PLANT_APPROVER_CR, VAN_E2_QMB_APPROVER_CR, VAN_CAMPAIGN_MANAGER_CR, ADMIN_CR, REPORTER_CR],
  },
  [CR_PERMISSION_ACTIONS.FULL_CR]: {
    [statusCR.IN_CREATION]: [CREATOR_CR, CREATOR_EXTERNAL_CR, REQUESTER_CR, ADMIN_CR, STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR,
      REPORTER_CR],
    [statusCR.IN_CAUSER_APPROVAL]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR,
      STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, APPROVER_CR, CENTER_VIEWER_CR, REPORTER_CR,
      CAUSER_CHECK_APPROVER_CR, INFORMER_CHECK_APPROVER_CR, CAUSER_PRE_APPROVER_CR, INFORMER_PRE_APPROVER_CR],
    [statusCR.IN_INFORMER_APPROVAL]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR,
      STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, APPROVER_CR, CENTER_VIEWER_CR, REPORTER_CR,
      CAUSER_CHECK_APPROVER_CR, INFORMER_CHECK_APPROVER_CR, CAUSER_PRE_APPROVER_CR, INFORMER_PRE_APPROVER_CR],
    [statusCR.IN_CAUSER_PRE_APPROVAL]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR,
      STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, CAUSER_PRE_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CENTER_VIEWER_CR, REPORTER_CR],
    [statusCR.IN_INFORMER_PRE_APPROVAL]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR,
      STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, CAUSER_PRE_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CENTER_VIEWER_CR, REPORTER_CR],
    [statusCR.IN_CAUSER_CHECK]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR,
      STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, CAUSER_CHECK_APPROVER_CR, INFORMER_CHECK_APPROVER_CR, CENTER_VIEWER_CR, REPORTER_CR],
    [statusCR.IN_INFORMER_CHECK]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR,
      STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, CAUSER_CHECK_APPROVER_CR, INFORMER_CHECK_APPROVER_CR, CENTER_VIEWER_CR, REPORTER_CR],
    [statusCR.IN_PLANT_VALIDATION]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR,
      STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, APPROVER_CR, CAUSER_PRE_APPROVER_CR, INFORMER_PRE_APPROVER_CR,
      CAUSER_CHECK_APPROVER_CR, INFORMER_CHECK_APPROVER_CR, CENTER_VIEWER_CR, REPORTER_CR],
    [statusCR.IN_COMMITTEE]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR, STAND_IN_CR,
      CENTER_VIEWER_CR, REPORTER_CR],
    [statusCR.IN_PLANT_APPROVAL]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR,
      STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR, REPORTER_CR],
    [statusCR.CLOSED]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR, STAND_IN_CR,
      RD_COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, REPORTER_CR, VAN_CAMPAIGN_MANAGER_CR, VAN_COMMITTEE_COORDINATOR_CR,
      REPORTER_CR],
    [statusCR.DELETED]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR, STAND_IN_CR,
      RD_COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, REPORTER_CR],
    [statusCR.COMPLETED]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR, STAND_IN_CR,
      CENTER_VIEWER_CR, REPORTER_CR, CAUSER_CHECK_APPROVER_CR, INFORMER_CHECK_APPROVER_CR, CAUSER_PRE_APPROVER_CR,
      INFORMER_PRE_APPROVER_CR, APPROVER_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E2_QMB_APPROVER_CR, VAN_CAMPAIGN_MANAGER_CR,
      RD_COMMITTEE_COORDINATOR_CR],
    [statusCR.REQUESTED_FOR_CHANGES]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, ADMIN_CR, STAND_IN_CR,
      RD_COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, REPORTER_CR],
    [statusCR.IN_RD_COMMITTEE]: [CREATOR_CR, REQUESTER_CR, CAUSER_APPROVER_CR, INFORMER_APPROVER_CR, RD_COMMITTEE_COORDINATOR_CR,
      ADMIN_CR, STAND_IN_CR, CENTER_VIEWER_CR, REPORTER_CR, CAUSER_CHECK_APPROVER_CR, INFORMER_CHECK_APPROVER_CR,
      CAUSER_PRE_APPROVER_CR, INFORMER_PRE_APPROVER_CR, APPROVER_CR],
    [statusCR.IN_PREPARATION_FOR_REVIEW]: [ADMIN_CR, CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, REPORTER_CR],
    [statusCR.IN_REVIEW]: [ADMIN_CR, CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, REPORTER_CR],
    [statusCR.IN_VAN_CAUSER_APPROVAL]: [ADMIN_CR, CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, REPORTER_CR],
    [statusCR.IN_CARLINE_CONFIRMATION]: [ADMIN_CR, CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR,
      REPORTER_CR],
    [statusCR.IN_QMB_VAN_INFORMATION]: [ADMIN_CR, CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR,
      VAN_E3_QMB_PLANT_APPROVER_CR, VAN_E2_QMB_APPROVER_CR, VAN_CAMPAIGN_MANAGER_CR, REPORTER_CR],
    [statusCR.IN_VAN_PLANT_VALIDATION]: [CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR,
      VAN_E3_QMB_PLANT_APPROVER_CR, VAN_E2_QMB_APPROVER_CR, VAN_CAMPAIGN_MANAGER_CR, ADMIN_CR, REPORTER_CR],
    [statusCR.IN_PLANT_IMPLEMENTATION]: [
      ADMIN_CR, STAND_IN_CR, CAMPAIGN_RECIPIENT_CR, CREATOR_CR, CREATOR_EXTERNAL_CR, REQUESTER_CR, APPROVER_CR, INFORMER_APPROVER_CR, CREATOR_CR,
      CREATOR_EXTERNAL_CR, REQUESTER_CR, APPROVER_CR, INFORMER_APPROVER_CR, INFORMER_PRE_APPROVER_CR, INFORMER_CHECK_APPROVER_CR, CAMPAIGN_MANAGER_CR,
      COMMITTEE_COORDINATOR_CR, RD_COMMITTEE_COORDINATOR_CR, QMB_PLANT_APPROVER_CR, PLANT_SUPERVISOR_CR, CAUSER_APPROVER_CR, CAUSER_PRE_APPROVER_CR,
      CAUSER_CHECK_APPROVER_CR, CENTER_VIEWER_CR, REPORTER_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR,
      VAN_E3_QMB_PLANT_APPROVER_CR, VAN_E2_QMB_APPROVER_CR, VAN_CAMPAIGN_MANAGER_CR],
    [statusCR.IN_VAN_PLANT_IMPLEMENTATION]: [CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR,
      VAN_E3_QMB_PLANT_APPROVER_CR, VAN_E2_QMB_APPROVER_CR, VAN_CAMPAIGN_MANAGER_CR, ADMIN_CR, REPORTER_CR],
  },
  [CR_PERMISSION_ACTIONS.SEND_TO_APPROVAL]: {
    [statusCR.IN_CREATION]: [CREATOR_CR, ADMIN_CR, STAND_IN_CR],
  },
  [CR_PERMISSION_ACTIONS.SEND_TO_REVIEW]: {
    [statusCR.IN_CREATION]: [CREATOR_CR, ADMIN_CR],
    [statusCR.IN_PREPARATION_FOR_REVIEW]: [ADMIN_CR],
    [statusCR.IN_REVIEW]: [ADMIN_CR],
  },
  [CR_PERMISSION_ACTIONS.MAKE_DECISION]: {
    [statusCR.IN_CAUSER_APPROVAL]: [CAUSER_APPROVER_CR],
    [statusCR.IN_INFORMER_APPROVAL]: [INFORMER_APPROVER_CR],
    [statusCR.IN_CAUSER_PRE_APPROVAL]: [CAUSER_PRE_APPROVER_CR],
    [statusCR.IN_INFORMER_PRE_APPROVAL]: [INFORMER_PRE_APPROVER_CR],
    [statusCR.IN_CAUSER_CHECK]: [CAUSER_CHECK_APPROVER_CR],
    [statusCR.IN_INFORMER_CHECK]: [INFORMER_CHECK_APPROVER_CR],
    [statusCR.IN_RD_COMMITTEE]: [RD_COMMITTEE_COORDINATOR_CR],
    [statusCR.IN_REVIEW]: [VAN_COMMITTEE_COORDINATOR_CR],
    [statusCR.IN_VAN_CAUSER_APPROVAL]: [VAN_E3_CAUSER_CR],
    [statusCR.IN_CARLINE_CONFIRMATION]: [VAN_E3_CHIEF_ENGINEER_CARLINE_CR],
  },
  [CR_PERMISSION_ACTIONS.ASSIGN]: {
    [statusCR.IN_PREPARATION_FOR_REVIEW]: [ADMIN_CR, VAN_COMMITTEE_COORDINATOR_CR],
    [statusCR.IN_RD_COMMITTEE]: [RD_COMMITTEE_COORDINATOR_CR],
  },
  [CR_PERMISSION_ACTIONS.ASSIGN_TO]: {
    [statusCR.IN_PREPARATION_FOR_REVIEW]: [ADMIN_CR, VAN_COMMITTEE_COORDINATOR_CR],
    [statusCR.IN_RD_COMMITTEE]: [RD_COMMITTEE_COORDINATOR_CR],
  },
  [CR_PERMISSION_ACTIONS.CONFIRM_KNOWLEDGE]: {
    [statusCR.IN_VAN_PLANT_VALIDATION]: [VAN_E2_QMB_APPROVER_CR],
  },
  [CR_PERMISSION_ACTIONS.COPY_SUB_CR]: {
    [statusSubCR.IN_PLANT_APPROVAL]: [ADMIN_CR, CREATOR_CR],
    [statusSubCR.IN_PLANT_PREPARATION]: [ADMIN_CR, CREATOR_CR, VAN_CAMPAIGN_MANAGER_CR],
    [statusSubCR.IN_PLANT_COORDINATION]: [ADMIN_CR, CREATOR_CR],
    [statusSubCR.IN_VAN_PLANT_COORDINATION]: [ADMIN_CR, CREATOR_CR, VAN_CAMPAIGN_MANAGER_CR],
    [statusSubCR.DENIED]: [ADMIN_CR, CREATOR_CR, VAN_CAMPAIGN_MANAGER_CR],
  },
  [CR_PERMISSION_ACTIONS.ASSIGN_MANAGER_OVERVIEW]: assignManagerPermissions,
  [CR_PERMISSION_ACTIONS.ASSIGN_MANAGER]: assignManagerPermissions,
  [CR_PERMISSION_ACTIONS.MAKE_DECISION_SUB_CR]: {
    [WORKFLOW_TYPE.DIGITAL]: {
      [statusSubCR.IN_CHECKING]: [CAMPAIGN_MANAGER_CR, ADMIN_CR],
      [statusSubCR.IN_E3_APPROVAL]: [QMB_PLANT_APPROVER_CR],
    },
    [WORKFLOW_TYPE.NOT_READY]: {
      [statusSubCR.IN_ANALYSIS]: [CAMPAIGN_MANAGER_CR, ADMIN_CR],
      [statusSubCR.IN_E3_APPROVAL]: [QMB_PLANT_APPROVER_CR],
      [statusSubCR.IN_E4_APPROVAL]: [E4_PLANT_SUPERVISOR_CR],
      [statusSubCR.IN_PLANT_APPROVAL]: [VAN_E3_QMB_PLANT_APPROVER_CR],
    },
  },
  [CR_PERMISSION_ACTIONS.SEND_TO_APPROVAL_SUB_CR]: {
    [WORKFLOW_TYPE.DIGITAL]: {
      [statusSubCR.IN_ANALYSIS]: [CAMPAIGN_MANAGER_CR, ADMIN_CR, STAND_IN_CR],
    },
    [WORKFLOW_TYPE.NOT_READY]: {
      [statusSubCR.IN_VAN_PLANT_COORDINATION]: [VAN_CAMPAIGN_MANAGER_CR],
    },
  },
  [CR_PERMISSION_ACTIONS.FULL_CR_SUB_CR]: {
    [statusSubCR.OPEN]: [CREATOR_CR, COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, CAMPAIGN_MANAGER_CR,
      CAUSER_PRE_APPROVER_CR, INFORMER_CHECK_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, ADMIN_CR, CAMPAIGN_RECIPIENT_CR],
    [statusSubCR.IN_ANALYSIS]:
      [CREATOR_CR, COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, CAMPAIGN_MANAGER_CR, CAUSER_PRE_APPROVER_CR,
        INFORMER_CHECK_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, ADMIN_CR, CAMPAIGN_RECIPIENT_CR, QMB_PLANT_APPROVER_CR],
    [statusSubCR.IN_E4_APPROVAL]:
      [CREATOR_CR, COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, CAMPAIGN_MANAGER_CR, CAUSER_PRE_APPROVER_CR,
        INFORMER_CHECK_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, E4_PLANT_SUPERVISOR_CR, QMB_PLANT_APPROVER_CR,
        ADMIN_CR, CAMPAIGN_RECIPIENT_CR],
    [statusSubCR.IN_E3_APPROVAL]:
      [CREATOR_CR, COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, CAMPAIGN_MANAGER_CR, CAUSER_PRE_APPROVER_CR,
        INFORMER_CHECK_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, E4_PLANT_SUPERVISOR_CR, QMB_PLANT_APPROVER_CR,
        ADMIN_CR, CAMPAIGN_RECIPIENT_CR],
    [statusSubCR.IN_PLANT_VALIDATION]:
      [CREATOR_CR, COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, CAMPAIGN_MANAGER_CR, CAUSER_PRE_APPROVER_CR,
        INFORMER_CHECK_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, ADMIN_CR, CAMPAIGN_RECIPIENT_CR],
    [statusSubCR.ACCEPTED]:
      [CREATOR_CR, COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, CAMPAIGN_MANAGER_CR, CAUSER_PRE_APPROVER_CR,
        INFORMER_CHECK_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, E4_PLANT_SUPERVISOR_CR, QMB_PLANT_APPROVER_CR,
        ADMIN_CR, CAMPAIGN_RECIPIENT_CR],
    [statusSubCR.DENIED]:
      [CREATOR_CR, COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, CAMPAIGN_MANAGER_CR, CAUSER_PRE_APPROVER_CR,
        INFORMER_CHECK_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, E4_PLANT_SUPERVISOR_CR, QMB_PLANT_APPROVER_CR,
        ADMIN_CR, CAMPAIGN_RECIPIENT_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR,
        VAN_E3_QMB_PLANT_APPROVER_CR, VAN_CAMPAIGN_MANAGER_CR],
    [statusSubCR.IN_PLANT_COORDINATION]: [COMMITTEE_COORDINATOR_CR, ADMIN_CR, CAMPAIGN_RECIPIENT_CR, CREATOR_CR],
    [statusSubCR.IN_VAN_PLANT_COORDINATION]: [ADMIN_CR, CAMPAIGN_RECIPIENT_CR, CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR,
      VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR, VAN_E3_QMB_PLANT_APPROVER_CR, VAN_CAMPAIGN_MANAGER_CR],
    [statusSubCR.IN_PLANT_APPROVAL]: [ADMIN_CR, CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR,
      VAN_E3_QMB_PLANT_APPROVER_CR],
    [statusSubCR.IN_PLANT_PREPARATION]: [ADMIN_CR, CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR,
      VAN_E3_QMB_PLANT_APPROVER_CR, VAN_CAMPAIGN_MANAGER_CR],
  },
  [CR_PERMISSION_ACTIONS.SHOW_SUMMARY_SUB_CR]: {
    [statusSubCR.OPEN]: [CREATOR_CR, COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, CAMPAIGN_MANAGER_CR,
      CAUSER_PRE_APPROVER_CR, INFORMER_CHECK_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, ADMIN_CR, CAMPAIGN_RECIPIENT_CR],
    [statusSubCR.IN_ANALYSIS]:
      [CREATOR_CR, COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, CAMPAIGN_MANAGER_CR, CAUSER_PRE_APPROVER_CR,
        INFORMER_CHECK_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, ADMIN_CR, CAMPAIGN_RECIPIENT_CR, QMB_PLANT_APPROVER_CR],
    [statusSubCR.IN_E4_APPROVAL]:
      [CREATOR_CR, COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, CAMPAIGN_MANAGER_CR, CAUSER_PRE_APPROVER_CR,
        INFORMER_CHECK_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, E4_PLANT_SUPERVISOR_CR, QMB_PLANT_APPROVER_CR,
        ADMIN_CR, CAMPAIGN_RECIPIENT_CR],
    [statusSubCR.IN_E3_APPROVAL]:
      [CREATOR_CR, COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, CAMPAIGN_MANAGER_CR, CAUSER_PRE_APPROVER_CR,
        INFORMER_CHECK_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, E4_PLANT_SUPERVISOR_CR, QMB_PLANT_APPROVER_CR,
        ADMIN_CR, CAMPAIGN_RECIPIENT_CR],
    [statusSubCR.IN_PLANT_VALIDATION]:
      [CREATOR_CR, COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, CAMPAIGN_MANAGER_CR, CAUSER_PRE_APPROVER_CR,
        INFORMER_CHECK_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, ADMIN_CR, CAMPAIGN_RECIPIENT_CR],
    [statusSubCR.ACCEPTED]:
      [CREATOR_CR, COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, CAMPAIGN_MANAGER_CR, CAUSER_PRE_APPROVER_CR,
        INFORMER_CHECK_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, E4_PLANT_SUPERVISOR_CR, QMB_PLANT_APPROVER_CR,
        ADMIN_CR, CAMPAIGN_RECIPIENT_CR],
    [statusSubCR.DENIED]:
      [CREATOR_CR, COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, CAMPAIGN_MANAGER_CR, CAUSER_PRE_APPROVER_CR,
        INFORMER_CHECK_APPROVER_CR, INFORMER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, E4_PLANT_SUPERVISOR_CR, QMB_PLANT_APPROVER_CR,
        ADMIN_CR, CAMPAIGN_RECIPIENT_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR,
        VAN_E3_QMB_PLANT_APPROVER_CR, VAN_CAMPAIGN_MANAGER_CR],
    [statusSubCR.IN_PLANT_COORDINATION]: [COMMITTEE_COORDINATOR_CR, ADMIN_CR, CAMPAIGN_RECIPIENT_CR, CREATOR_CR],
    [statusSubCR.IN_VAN_PLANT_COORDINATION]: [ADMIN_CR, CAMPAIGN_RECIPIENT_CR, CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR,
      VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR, VAN_E3_QMB_PLANT_APPROVER_CR, VAN_CAMPAIGN_MANAGER_CR],
    [statusSubCR.IN_PLANT_APPROVAL]: [ADMIN_CR, CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR,
      VAN_E3_QMB_PLANT_APPROVER_CR],
    [statusSubCR.IN_PLANT_PREPARATION]: [ADMIN_CR, CREATOR_CR, VAN_COMMITTEE_COORDINATOR_CR, VAN_E3_CAUSER_CR, VAN_E3_CHIEF_ENGINEER_CARLINE_CR,
      VAN_E3_QMB_PLANT_APPROVER_CR, VAN_CAMPAIGN_MANAGER_CR],
  },
  [CR_PERMISSION_ACTIONS.COMPLETE_SUB_CR]: {
    [WORKFLOW_TYPE.NOT_READY]: {
      [statusSubCR.IN_VAN_PLANT_COORDINATION]: [ADMIN_CR, VAN_CAMPAIGN_MANAGER_CR],
    },
  },
  [CR_PERMISSION_ACTIONS.ASSIGN_CREATOR]: {
    [statusCR.IN_CREATION]: [CREATOR_CR, CREATOR_EXTERNAL_CR, ADMIN_CR],
  },
  [CR_PERMISSION_ACTIONS.TRANSLATIONS_TAB]: {
    [statusCR.IN_CREATION]: [CREATOR_CR, CREATOR_EXTERNAL_CR, ADMIN_CR, CENTER_VIEWER_CR, REPORTER_CR, RD_COMMITTEE_COORDINATOR_CR],
  },
  [CR_PERMISSION_ACTIONS.PLANT_FEEDBACK_TAB]: {
    [statusCR.IN_PLANT_VALIDATION]: [CREATOR_CR, COMMITTEE_COORDINATOR_CR, CENTER_VIEWER_CR, REPORTER_CR, CAUSER_CHECK_APPROVER_CR, INFORMER_CHECK_APPROVER_CR,
      CAUSER_PRE_APPROVER_CR, INFORMER_PRE_APPROVER_CR, QMB_PLANT_APPROVER_CR, APPROVER_CR, ADMIN_CR],
  },
  [CR_PERMISSION_ACTIONS.SPLIT_SUB_CAMPAIGN]: {
    [WORKFLOW_TYPE.DIGITAL]: {
      [statusSubCR.IN_ANALYSIS]: [CAMPAIGN_MANAGER_CR, ADMIN_CR],
    },
    [WORKFLOW_TYPE.NOT_READY]: {
      [statusSubCR.IN_ANALYSIS]: [CAMPAIGN_MANAGER_CR, ADMIN_CR],
    },
  },
  [CR_PERMISSION_ACTIONS.SHOW_CHANGES]: {
    [statusCR.IN_CREATION]: [ADMIN_CR, CREATOR_CR, STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR],
    [statusCR.IN_RD_COMMITTEE]: [ADMIN_CR, CREATOR_CR, STAND_IN_CR, RD_COMMITTEE_COORDINATOR_CR],
  },
  [CR_PERMISSION_ACTIONS.UPLOAD_ATTACHMENT]: {
    [statusCR.IN_PLANT_IMPLEMENTATION]: [CAMPAIGN_MANAGER_CR, COMMITTEE_COORDINATOR_CR, ADMIN_CR, CREATOR_CR, STAND_IN_CR],
    [statusCR.IN_VAN_PLANT_IMPLEMENTATION]: [ADMIN_CR, CREATOR_CR, STAND_IN_CR, VAN_CAMPAIGN_MANAGER_CR, VAN_COMMITTEE_COORDINATOR_CR],
  },
};

export default matrix;
